/**
 * API Configuration
 * 
 * Provides configuration parameters for API requests
 */

/**
 * Configuration options for API requests
 */
export interface ApiConfig {
  /** Base URL for API requests */
  baseUrl: string;
  
  /** Default headers to include with all requests */
  defaultHeaders: Record<string, string>;
  
  /** Default timeout in milliseconds */
  timeout: number;
}

/**
 * Global API configuration
 */
let apiConfig: ApiConfig = {
  baseUrl: process.env.REACT_APP_API_URL || '',
  defaultHeaders: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 30000 // Default timeout of 30 seconds
};

/**
 * Sets the API configuration
 * 
 * @param config Configuration options
 */
export function setApiConfig(config: Partial<ApiConfig>): void {
  apiConfig = { ...apiConfig, ...config };
}

/**
 * Gets the current API configuration
 * 
 * @returns The current API configuration
 */
export function getApiConfig(): ApiConfig {
  return { ...apiConfig };
}

/**
 * Gets the base URL for API requests
 * 
 * @returns The base URL from configuration
 */
export function getApiBaseUrl(): string {
  return apiConfig.baseUrl;
}

/**
 * Constructs a full API URL using the base URL
 * 
 * @param path The API path to append to the base URL
 * @returns The full API URL
 */
export function getApiUrl(path: string): string {
  const baseUrl = apiConfig.baseUrl;
  
  // If the path already includes the protocol, return it as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // If we have a base URL and it doesn't end with slash, add one
  if (baseUrl && !baseUrl.endsWith('/')) {
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  } else {
    // If no base URL or it already ends with slash, just append the path
    return `${baseUrl}${path}`;
  }
}